<script lang="ts" setup>
import type { ButtonColor } from '@/components/button/SkrButton.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import SkrDivider from '@/components/SkrDivider.vue'
import DialogLoadingBox from '@/components/dialogs/DialogLoadingBox.vue'

interface Props {
  title: string
  maxWidth?: string
  actionText?: string
  actionColor?: ButtonColor
  cancelText?: string
  cancelColor?: ButtonColor
  disabled?: boolean
  loading?: boolean
  persistent?: boolean
  dataCy?: string
  divider?: boolean
  centered?: boolean
  // TODO: once we refactor wave to use the new dialog, remove this prop and associated styles. We should implement a new prop that allows more flexible padding/margin for each dialog or each dialog should be designed with the same padding/margin
  refreshed?: boolean
  isContentLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maxWidth: '570',
  isContentLoading: false,
})

const emit = defineEmits<{
  close: []
  action: []
}>()

const { t } = useI18n()

const { isLoaderShown, isContentShown } = useLoaderVisibility(() => props.isContentLoading)
const isDialogOpen = computed(() => isLoaderShown.value || isContentShown.value)
const dialogMaxWidth = computed(() => (isLoaderShown.value ? 'fit-content' : props.maxWidth))

const onOpenChange = (isOpen: boolean) => {
  if (!isOpen) emit('close')
}
</script>

<template>
  <!-- scroll-strategy on v-dialog set to 'none' because of a bug in v-dialog: the v-overlay inside v-dialog is not mounted on time 
   before the dialog tries to access scrolling behavior on the overlay  -->
  <v-dialog
    :model-value="isDialogOpen"
    :max-width="dialogMaxWidth"
    :persistent="persistent"
    :close-on-back="true"
    scroll-strategy="none"
    @update:model-value="onOpenChange"
  >
    <transition name="dialog-reveal">
      <dialog-loading-box v-if="isLoaderShown" />
      <div
        v-else
        class="base-dialog"
        :class="{
          'base-dialog--with-divider': divider,
          'base-dialog--center': centered,
          'base-dialog--refreshed': refreshed,
        }"
        :data-cy="dataCy"
      >
        <slot name="header" />
        <h2 class="base-dialog__title">
          <slot name="title">{{ title }}</slot>
        </h2>
        <div class="base-dialog__content">
          <slot />
        </div>
        <slot name="actions">
          <skr-divider v-if="divider" class="base-dialog__divider" />
          <div class="base-dialog__actions">
            <skr-button
              data-cy="dialog_cancel_button"
              type="secondary"
              size="xl"
              :color="cancelColor"
              @click="emit('close')"
            >
              {{ cancelText ?? t('global.no_thanks') }}
            </skr-button>
            <skr-button
              data-cy="dialog_action_button"
              size="xl"
              :color="actionColor"
              :disabled="disabled"
              :loading="loading"
              @click="emit('action')"
            >
              {{ actionText ?? t('global.accept') }}
            </skr-button>
          </div>
        </slot>
      </div>
    </transition>
  </v-dialog>
</template>

<style lang="scss" scoped>
.dialog-reveal-enter-active {
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.dialog-reveal-enter-from {
  transform: scale(0.9);
}
.dialog-reveal-leave-active {
  display: none;
}
@media (prefers-reduced-motion: reduce) {
  .dialog-reveal-enter-active {
    transition: none;
  }
}

.base-dialog {
  padding: 9 * $bw 12 * $bw;
  background-color: $c-white;
  border-radius: 30px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  > * {
    flex: 0 0 auto;
  }
  &__title {
    color: $c-grey-darker;
    margin-top: 4 * $bw;
    margin-bottom: 4 * $bw;
    white-space: normal;
    &:not(:first-child) {
      margin-top: 0;
    }
  }
  &__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    // negative margin so the scrollbar goes on the dialog's border, not the border of the content
    margin-inline: -12 * $bw;
    padding-inline: 12 * $bw;
  }
  &__divider {
    margin: 9 * $bw -12 * $bw 0;
  }
  &__actions {
    margin-top: 12 * $bw;
    display: flex;
    justify-content: flex-end;
    gap: 4 * $bw;
  }
  &--with-divider &__actions {
    margin-top: 6 * $bw;
  }
  // `centered` centers the whole dialog — title, content and both buttons.
  &--center &__title,
  &--center &__content {
    text-align: center;
  }
  &--center &__actions {
    justify-content: center;
  }

  &--refreshed {
    padding-top: 0;
    padding-bottom: 6 * $bw;
    border-radius: 16px;
  }
  &--refreshed &__title {
    margin-top: 12 * $bw;
  }
  &--refreshed#{&}--center {
    padding-bottom: 12 * $bw;
  }
  // refreshed + divider renders a full-bleed footer below the divider
  &--refreshed#{&}--with-divider {
    padding-bottom: 0;
  }
  &--refreshed#{&}--with-divider &__divider {
    margin-top: 12 * $bw;
  }
  &--refreshed#{&}--with-divider &__actions {
    margin: 0 -12 * $bw;
    padding: 6 * $bw 12 * $bw;
    .v-btn {
      height: 12 * $bw;
    }
  }
}
</style>
